import { EnvironmentHttpCommonError, OrchestrationV2ShellSnapshot } from "@heydex/contracts";
import * as Cause from "effect/Cause";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { HttpClient, HttpClientError, type HttpClientResponse } from "effect/http";

import { environmentEndpointUrl } from "../environment/endpoint.ts";
import { executeEnvironmentHttpRequest, makeEnvironmentHttpApiGroupClient } from "../rpc/http.ts";
import type { PreparedConnection } from "../connection/model.ts";
import {
  executeEnvironmentGroupRequest,
  withOrchestrationProtocolHeader,
} from "./environmentHttp.ts";

// Long enough for a busy runtime to finish. On timeout the RPC subscription asks the
// same runtime for the same full snapshot, so a short deadline only throws the first
// build away. A stopped runtime ends the RPC session, which interrupts this load. The
// cached shell renders while this runs.
const DEFAULT_SHELL_SNAPSHOT_TIMEOUT_MS = 20_000;

/**
 * Load the environment shell snapshot (projects + thread shells) over HTTP instead of
 * as the RPC subscription's first frame.
 */
export const fetchEnvironmentShellSnapshot = Effect.fn(
  "clientRuntime.state.fetchEnvironmentShellSnapshot",
)(function* (input: { readonly prepared: PreparedConnection; readonly timeoutMs?: number }) {
  return yield* executeEnvironmentGroupRequest({
    ...input,
    group: "orchestration",
    method: "GET",
    url: (urls) => urls.shellSnapshot(),
    timeoutMs: input.timeoutMs ?? DEFAULT_SHELL_SNAPSHOT_TIMEOUT_MS,
    // The body is decoded here so each thread's pull request links can decode after its
    // rows. Every declared error is an `EnvironmentHttpCommonError`, so decoding one keeps
    // the error the credential retry checks for.
    request: ({ client }) =>
      client
        .shellSnapshot({
          headers: withOrchestrationProtocolHeader(),
          responseMode: "response-only",
        })
        .pipe(Effect.flatMap(decodeShellSnapshotResponse)),
  });
});

/** The boot read needs only the base URL, before discovery supplies an environment id. */
export const fetchPrimaryEnvironmentShellSnapshot = Effect.fn(
  "clientRuntime.state.fetchPrimaryEnvironmentShellSnapshot",
)(function* (httpBaseUrl: string) {
  const client = yield* makeEnvironmentHttpApiGroupClient(httpBaseUrl, "orchestration");
  return yield* executeEnvironmentHttpRequest(
    environmentEndpointUrl(httpBaseUrl, "/api/orchestration/shell"),
    DEFAULT_SHELL_SNAPSHOT_TIMEOUT_MS,
    client
      .shellSnapshot({
        headers: withOrchestrationProtocolHeader(),
        responseMode: "response-only",
      })
      .pipe(Effect.flatMap(decodeShellSnapshotResponse)),
  );
});

const decodeEnvironmentHttpCommonError = Schema.decodeUnknownEffect(
  Schema.toCodecJson(EnvironmentHttpCommonError),
);

const decodeShellSnapshotJson = Schema.decodeUnknownEffect(
  Schema.toCodecJson(OrchestrationV2ShellSnapshot),
);

const decodeShellSnapshotResponse = (
  response: HttpClientResponse.HttpClientResponse,
): Effect.Effect<
  OrchestrationV2ShellSnapshot,
  HttpClientError.HttpClientError | Schema.SchemaError | EnvironmentHttpCommonError
> => {
  if (response.status === 200) {
    return response.json.pipe(Effect.flatMap(decodeShellSnapshotJson));
  }
  // A body that is not a declared error keeps its status, as the generated decoder reported.
  const statusError = new HttpClientError.HttpClientError({
    reason: new HttpClientError.StatusCodeError({ request: response.request, response }),
  });
  return response.json.pipe(
    Effect.flatMap(decodeEnvironmentHttpCommonError),
    Effect.matchEffect({
      onFailure: () => Effect.fail(statusError),
      onSuccess: Effect.fail,
    }),
  );
};

/**
 * Loads the environment shell snapshot over HTTP, returning `Option.none()` when
 * it cannot be loaded (so the caller falls back to the snapshot the RPC subscription sends).
 * Decouples the shell state machine from the underlying HTTP details and
 * keeps them out of test contexts.
 */
export class ShellSnapshotLoader extends Context.Service<
  ShellSnapshotLoader,
  {
    readonly load: (
      prepared: PreparedConnection,
    ) => Effect.Effect<Option.Option<OrchestrationV2ShellSnapshot>>;
  }
>()("@heydex/client-runtime/state/shellSnapshotHttp/ShellSnapshotLoader") {}

export const layer: Layer.Layer<ShellSnapshotLoader, never, HttpClient.HttpClient> = Layer.effect(
  ShellSnapshotLoader,
  Effect.gen(function* () {
    const httpClient = yield* HttpClient.HttpClient;
    return ShellSnapshotLoader.of({
      load: (prepared: PreparedConnection) =>
        fetchEnvironmentShellSnapshot({ prepared }).pipe(
          Effect.map(Option.some<OrchestrationV2ShellSnapshot>),
          Effect.provideService(HttpClient.HttpClient, httpClient),
          Effect.catchCause((cause) =>
            Effect.logWarning(
              "Could not load the environment shell snapshot over HTTP; using the RPC snapshot instead.",
            ).pipe(
              Effect.annotateLogs({ cause: Cause.pretty(cause) }),
              Effect.as(Option.none<OrchestrationV2ShellSnapshot>()),
            ),
          ),
        ),
    });
  }),
);
